import { Link, useLocation, useNavigate } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { useSiteInfo } from "@/SiteInfo/hooks/use-site-info";
import { parseSocialLinks } from "@/SiteInfo/lib/social-links";
import { AppStoreButtons } from "@/SiteInfo/components/AppStoreButtons";
import { CouponAuthModal } from "@/Coupons/components/CouponAuthModal";
import { useCouponAuth } from "@/Coupons/hooks/use-coupon-auth";
import { TermsOfServiceSheet, PrivacyPolicySheet } from "@/components/Legal";
import { CookiePreferencesSheet } from "@/components/CookieConsent/CookiePreferencesSheet";
import { useMemo, useState, useEffect } from "react";
import { IconCircleCheck, IconX } from '@tabler/icons-react';

export const Footer = () => {
  const { data: siteInfo } = useSiteInfo();
  const { isAuthenticated } = useCouponAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [showLoggedInToast, setShowLoggedInToast] = useState(false);
  const [termsSheetOpen, setTermsSheetOpen] = useState(false);
  const [privacySheetOpen, setPrivacySheetOpen] = useState(false);
  const [cookiePrefsOpen, setCookiePrefsOpen] = useState(false);

  // Auto-open sheets based on URL path
  useEffect(() => {
    if (location.pathname === '/terms') {
      setTermsSheetOpen(true);
    } else if (location.pathname === '/privacy') {
      setPrivacySheetOpen(true);
    }
  }, [location.pathname]);

  // Handle sheet close - navigate back to home if opened via direct URL
  const handleTermsSheetChange = (open: boolean) => {
    setTermsSheetOpen(open);
    if (!open && location.pathname === '/terms') {
      navigate('/', { replace: true });
    }
  };

  const handlePrivacySheetChange = (open: boolean) => {
    setPrivacySheetOpen(open);
    if (!open && location.pathname === '/privacy') {
      navigate('/', { replace: true });
    }
  };

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (showLoggedInToast) {
      const timer = setTimeout(() => {
        setShowLoggedInToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showLoggedInToast]);

  const socialLinks = useMemo(() => {
    if (!siteInfo?.SiteExternalLink) return [];
    const urls = siteInfo.SiteExternalLink.map((link) => link.SiteExternalLink);
    return parseSocialLinks(urls);
  }, [siteInfo?.SiteExternalLink]);

  const siteName = siteInfo?.SiteTitle ?? "Store";
  const siteTagLine = siteInfo?.SiteTagLine ?? "Fresh Savings Daily";
  const siteDetails = siteInfo?.SiteDetails ?? "Fresh local groceries with delivery, pickup, and weekly savings.";
  const inverseLogo = siteInfo?.SiteInverseLogo?.url;
  const hasCoupons = siteInfo?.SiteHasCoupons ?? false;
  const hasMobileApp = siteInfo?.SiteHasMobileApp ?? false;
  const hasAboutUs = import.meta.env.VITE_FEATURE_ABOUT_US === 'true';
  const hasCareers = import.meta.env.VITE_FEATURE_CAREERS === 'true';
  const careersUrl = import.meta.env.VITE_CAREERS_URL;
  const appStoreUrl = siteInfo?.AppStoreURL;
  const googlePlayUrl = siteInfo?.GooglePlayStoreURL;

  return (
    <footer className="bg-brand-secondary-500 text-neutral-50">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr_1fr] gap-8">
          {/* Brand - 40% */}
          <div className="col-span-2 md:col-span-1">
            {inverseLogo ? (
              <img
                src={inverseLogo}
                alt={siteName}
                className="h-10 w-auto mb-4"
              />
            ) : (
              <h3 className="font-bold text-xl mb-4">{siteName}</h3>
            )}
            <p className="text-sm opacity-90 mb-4">{siteDetails}</p>
            {hasMobileApp && (
              <AppStoreButtons
                appStoreUrl={appStoreUrl}
                googlePlayUrl={googlePlayUrl}
                siteName={siteName}
              />
            )}
          </div>

          {/* Quick Links - 20% */}
          <div>
            <h3 className="font-bold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm hover:underline opacity-90">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/ads" className="text-sm hover:underline opacity-90">
                  Weekly Ads
                </Link>
              </li>
              <li>
                <Link to="/locations" className="text-sm hover:underline opacity-90">
                  Store Locations
                </Link>
              </li>
              <li>
                <Link to="/recipes" className="text-sm hover:underline opacity-90">
                  Recipes
                </Link>
              </li>
              {hasCoupons && (
                <li>
                  <Link to="/coupons" className="text-sm hover:underline opacity-90">
                    Digital Coupons
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* My Account - 20% (only if coupons enabled) */}
          {hasCoupons && (
            <div>
              <h3 className="font-bold text-lg mb-4">My Account</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => {
                      if (isAuthenticated) {
                        setShowLoggedInToast(true);
                      } else {
                        setAuthModalOpen(true);
                      }
                    }}
                    className="text-sm hover:underline opacity-90"
                  >
                    Sign In
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      if (isAuthenticated) {
                        setShowLoggedInToast(true);
                      } else {
                        setAuthModalOpen(true);
                      }
                    }}
                    className="text-sm hover:underline opacity-90"
                  >
                    Register
                  </button>
                </li>
                {isAuthenticated && (
                  <li>
                    <Link to="/coupons?tab=clipped" className="text-sm hover:underline opacity-90">
                      My Coupons
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Help & Info - 20% */}
          <div>
            <h3 className="font-bold text-lg mb-4">Help & Info</h3>
            <ul className="space-y-2">
              {hasAboutUs && (
                <li>
                  <Link to="/about" className="text-sm hover:underline opacity-90">
                    About Us
                  </Link>
                </li>
              )}
              <li>
                <Link to="/contact" className="text-sm hover:underline opacity-90">
                  Contact Us
                </Link>
              </li>
              {hasCareers && careersUrl && (
                <li>
                  <a
                    href={careersUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:underline opacity-90"
                  >
                    Careers
                  </a>
                </li>
              )}
              <li>
                <button
                  onClick={() => setPrivacySheetOpen(true)}
                  className="text-sm hover:underline opacity-90"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  onClick={() => setTermsSheetOpen(true)}
                  className="text-sm hover:underline opacity-90"
                >
                  Terms of Service
                </button>
              </li>
              <li>
                <button
                  onClick={() => setCookiePrefsOpen(true)}
                  className="text-sm hover:underline opacity-90"
                >
                  Your Privacy Choices
                </button>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-8 bg-primary-foreground/20" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-1 md:gap-2 text-sm opacity-75">
            <span>© {new Date().getFullYear()} {siteName} | {siteTagLine} | <a href="https://allianceretailgroup.com" target="_blank" rel="noopener noreferrer">Powered by ARG</a></span>
          </div>

          {socialLinks.length > 0 && (
            <div className="flex gap-3 md:ml-auto">
              {socialLinks.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg hover:opacity-75 transition-opacity"
                  aria-label={link.label}
                >
                  <link.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Coupon Auth Modal */}
      <CouponAuthModal
        coupon={null}
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
      />

      {/* Legal Sheets */}
      <TermsOfServiceSheet
        open={termsSheetOpen}
        onOpenChange={handleTermsSheetChange}
      />
      <PrivacyPolicySheet
        open={privacySheetOpen}
        onOpenChange={handlePrivacySheetChange}
      />
      <CookiePreferencesSheet
        open={cookiePrefsOpen}
        onOpenChange={setCookiePrefsOpen}
      />

      {/* Logged In Toast Notification */}
      {showLoggedInToast && (
        <div className="fixed top-4 right-4 z-50 w-full max-w-sm animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="bg-white rounded-lg shadow-lg border border-border overflow-hidden">
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10 flex-shrink-0 w-9 h-9">
                  <IconCircleCheck className="h-5 w-5 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground">Already Signed In</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    You are already logged into your account.
                  </p>
                </div>
                <button
                  onClick={() => setShowLoggedInToast(false)}
                  className="p-1 rounded-md hover:bg-muted flex-shrink-0"
                >
                  <IconX className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};
